import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { Card, Modal } from "react-bootstrap";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { security_key, BASE_URL } from "../global";
import DiagnosticsMappingCard from "./DiagnosticsMappingCard";

const API = BASE_URL + "API/api_index.php";

// Attempt shapes differ between math (integer ids, date_attempted, score_percent)
// and english (nanoid ids, created_at). Extract defensively.
const getAttemptId = (r) => r.diagnostic_quiz_attempt_id ?? r.attempt_id ?? r.id ?? null;
// Math attempts use snake_case (date_attempted, score_percent); english uses camelCase
// (createdAt) with details nested under `quiz` and no attempt-level score.
const getDate = (r) => r.date_attempted ?? r.createdAt ?? r.created_at ?? r.date ?? "";
const getScore = (r) => {
    const v = r.score_percent ?? r.percent ?? r.score ?? null;
    return v === null || v === undefined || v === "" ? "-" : `${v}%`;
};
const getLevel = (r) =>
    r.level_name ??
    r.quiz?.name ??
    r.quiz?.levelId ??
    r.start_level_id ??
    r.level_id ??
    r.registered_year_level ??
    "-";

// "uk-y2" / "y3" -> "Year 2" / "Year 3"
const yearLabel = (lvl) => {
    const m = String(lvl || "").match(/y(\d+)/i);
    return m ? `Year ${m[1]}` : String(lvl || "");
};
const formatDate = (d) => {
    if (!d) return "-";
    const s = String(d);
    return s.includes("T") ? s.replace("T", " ").slice(0, 16) : s;
};

export default function ReportStudentDiagnostics() {
    const location = useLocation();
    const pkStudentKey = (location.state && location.state.pk_student_key) || "";
    const centreKey = sessionStorage.getItem("key");

    const [loading, setLoading] = useState(true);
    const [mapped, setMapped] = useState(false);
    const [rows, setRows] = useState([]);

    const [showReport, setShowReport] = useState(false);
    const [reportUrl, setReportUrl] = useState("");
    const [busyReport, setBusyReport] = useState(false);

    const fetchAttempts = useCallback(async () => {
        if (!centreKey || !pkStudentKey) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(API, {
                security_key,
                mode: "diagnostics_student_attempts",
                fk_tenat_key: centreKey,
                fk_student_key: pkStudentKey,
            });
            if (res?.data?.success) {
                setMapped(!!res.data.mapped);
                setRows(res.data.data || []);
            } else {
                toast.error(res?.data?.error || "Failed to load diagnostics attempts.");
                setRows([]);
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to load diagnostics attempts.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [centreKey, pkStudentKey]);

    useEffect(() => {
        fetchAttempts();
    }, [fetchAttempts]);

    // Math = one row per attempt. English = one row per year level (region+level),
    // because the diagnostics app aggregates a year level's tests into a single report.
    const displayRows = useMemo(() => {
        const mathRows = [];
        const engGroups = new Map();
        for (const r of rows || []) {
            if (r.subject === "english") {
                const region = r.quiz?.regionCode ?? r.regionCode ?? "";
                const level = r.quiz?.levelId ?? r.levelId ?? "";
                const date = r.createdAt ?? r.created_at ?? "";
                const key = `${region}|${level}`;
                const existing = engGroups.get(key);
                if (!existing) {
                    engGroups.set(key, {
                        id: `english-${key}`,
                        subject: "english",
                        date,
                        level: `${yearLabel(level)} English`.trim(),
                        score: "-",
                        _embed: { subject: "english", region, level },
                    });
                } else if (date > existing.date) {
                    existing.date = date; // show the most recent attempt date for the group
                }
            } else {
                const attemptId = getAttemptId(r);
                mathRows.push({
                    id: `math-${attemptId}`,
                    subject: "math",
                    date: getDate(r),
                    level: getLevel(r),
                    score: getScore(r),
                    _embed: { subject: "math", attempt_id: attemptId },
                });
            }
        }
        return [...mathRows, ...Array.from(engGroups.values())];
    }, [rows]);

    const viewReport = async (row) => {
        const embed = row._embed || {};
        setBusyReport(true);
        try {
            const res = await axios.post(API, {
                security_key,
                mode: "diagnostics_report_embed_url",
                fk_tenat_key: centreKey,
                fk_student_key: pkStudentKey,
                ...embed, // {subject, attempt_id} for math | {subject, region, level} for english
            });
            if (res?.data?.success && res.data.url) {
                setReportUrl(res.data.url);
                setShowReport(true);
            } else {
                toast.error(res?.data?.error || "Could not build report link.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Could not open report.");
        } finally {
            setBusyReport(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                field: "subject",
                headerName: "Subject",
                minWidth: 120,
                valueGetter: (params) => (params.row.subject === "english" ? "English" : "Math"),
            },
            {
                field: "date",
                headerName: "Date",
                flex: 1,
                minWidth: 160,
                valueGetter: (params) => formatDate(params.row.date),
            },
            {
                field: "level",
                headerName: "Level",
                flex: 1,
                minWidth: 140,
                valueGetter: (params) => params.row.level || "-",
            },
            {
                field: "score",
                headerName: "Score",
                minWidth: 110,
                valueGetter: (params) => params.row.score ?? "-",
            },
            {
                field: "actions",
                headerName: "Report",
                sortable: false,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => (
                    <Button variant="contained" size="small" onClick={() => viewReport(params.row)} disabled={busyReport}>
                        View report
                    </Button>
                ),
            },
        ],
        [busyReport]
    );

    return (
        <>
            <ToastContainer position="top-center" />
            <div className="page-wrapper">
                <div className="mb-3 d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="page-title">Diagnostics Reports</h5>
                        <div role="presentation">
                            <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
                                <NavLink to="/calendar">Home</NavLink>
                                <NavLink to="/list-students">Students</NavLink>
                                <Typography color="text.primary">Diagnostics Reports</Typography>
                            </Breadcrumbs>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="contained" className="primary-filled" onClick={fetchAttempts}>
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Link status + management (shared with the student pages) */}
                {pkStudentKey ? (
                    <DiagnosticsMappingCard studentKey={pkStudentKey} showReportLink={false} onChanged={fetchAttempts} />
                ) : (
                    <Card className="my-card p-3 bg-white">
                        <div className="text-muted">No student selected. Open this page from a student's profile.</div>
                    </Card>
                )}

                {mapped ? (
                    <Card className="my-card p-2 bg-white">
                        <Card.Body>
                            <Box sx={{ width: "100%" }}>
                                <DataGrid
                                    autoHeight
                                    loading={loading}
                                    slots={{ loadingOverlay: LinearProgress, toolbar: GridToolbar }}
                                    slotProps={{ toolbar: { showQuickFilter: true } }}
                                    rows={displayRows}
                                    columns={columns}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                                    disableRowSelectionOnClick
                                    showCellVerticalBorder
                                    showColumnVerticalBorder
                                />
                            </Box>
                        </Card.Body>
                    </Card>
                ) : null}

                <Modal show={showReport} onHide={() => setShowReport(false)} fullscreen>
                    <Modal.Header closeButton>
                        <Modal.Title>Diagnostics Report</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: 0, display: "flex" }}>
                        {reportUrl ? (
                            <iframe
                                title="Diagnostics Report"
                                src={reportUrl}
                                style={{ flex: 1, width: "100%", height: "100%", border: "none" }}
                            />
                        ) : null}
                    </Modal.Body>
                </Modal>
            </div>
        </>
    );
}
